import type { ClarityWorkspaceLocation } from './models/clarity-workspace-location.model';

/** The query keys the workspace URL owns — everything else is carried verbatim (their TR-009). */
const OWNED_KEYS = ['doc', 'page', 'file', 'oi', 'action', 'domain', 'tab'] as const;

function setOrDelete(params: URLSearchParams, key: string, value: string | null): void {
	if (value !== null) {
		params.set(key, value);
	} else {
		params.delete(key);
	}
}

/** Ported from Clarity `apps/web/src/shared/utils/workspaceUrl.ts` (commit `f9217bb5a`). */
/**
 * Builds a workspace URL from a location, patching only the owned keys on `currentSearch` — brain's
 * `buildWorkspaceUrl`, unchanged. Every other parameter (`sd_*`, unknowns) is carried over verbatim.
 *
 * The answer is brain's own path — `/d/<domainId>/<tab>`, or `/` when `next.domainId` is `null`, with
 * every owned parameter dropped. Putting it under the Clarity section is the Angular integration's job,
 * not this rule's.
 */
export function buildClarityWorkspaceUrl(next: ClarityWorkspaceLocation, currentSearch: URLSearchParams): string {
	// domainId null → resolver route, drop all owned params
	if (next.domainId === null) {
		const out = new URLSearchParams(currentSearch);

		for (const key of OWNED_KEYS) {
			out.delete(key);
		}

		const qs = out.toString();

		return qs ? `/?${qs}` : '/';
	}

	const out = new URLSearchParams(currentSearch);

	// Always strip legacy query params — they live in the path now
	out.delete('domain');
	out.delete('tab');

	// Apply canonicalization before writing params
	let doc = next.doc;
	let page = next.page;

	// file wins over doc
	if (next.file && doc) {
		doc = null;
	}

	// page without doc is meaningless
	if (!doc) {
		page = null;
	}

	// Set or delete owned keys
	setOrDelete(out, 'doc', doc);
	setOrDelete(out, 'page', page !== null ? String(page) : null);
	setOrDelete(out, 'file', next.file);
	setOrDelete(out, 'oi', next.oi !== null ? String(next.oi) : null);
	setOrDelete(out, 'action', next.action);

	// The id is a path segment, not a query value: encode it so a `/`, `?` or `#` cannot reshape the route.
	const pathname = `/d/${encodeURIComponent(next.domainId)}/${next.tab}`;
	const qs = out.toString();

	return qs ? `${pathname}?${qs}` : pathname;
}
